// In-game sensitivity calculator (embedded in the Weapons tab). One instance
// per WeaponSettingsPanel so the game/DPI choice survives tab switches.
//
// Defaults tab: the "Default" pill is checked by default, so every picker
// change updates the global sensitivity live. Uncheck it to freeze the default
// while you pin weapons. The weapon pills pin overrides at the current value.
// Weapon tab: "Use for this weapon" stores that weapon's sensitivity override.

import { SENS_GAMES, type SensFovType } from "../sens-data";
import {
  OUR_YAW,
  cm360ToSens,
  defaultGameFov,
  fovToVfov,
  ourFov,
  sensToCm360,
  toOurSensitivity,
} from "../sens";
import { resolveWeapon, type GlobalDefaults, type WeaponSettings } from "../weapons/settings";
import { dsElement } from "./daisy";

export type SensScope = "defaults" | number;

export interface SensPickerCallbacks {
  /** `scope` is "defaults" (global sensitivity) or a weapon id (override). */
  onApply: (scope: SensScope, sens: number) => void;
  onFov: (fov: number) => void;
  /** Defaults tab pills: set/clear a weapon's sensitivity override. */
  onPin: (weapon: number, sens: number | null) => void;
}

/** Weapons offered as pin tags (id -> short label). */
const WEAPONS: Array<[number, string]> = [
  [1, "Melee"],
  [2, "Machinegun"],
  [3, "Shotgun"],
  [4, "Grenade"],
  [5, "Rocket"],
  [6, "Plasma"],
  [7, "Lightning"],
  [8, "Railgun"],
  [9, "Experimental"],
  [10, "Ball"],
];

const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));
const numText = (v: number): string => String(Number(v.toFixed(6)));

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  cls?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (cls) node.className = cls;
  if (text !== undefined) node.textContent = text;
  return node;
}

export class SensPicker {
  private game = "webrace";
  private dpi = 1600;
  private last: "cm" | "sens" = "cm";
  private scope: SensScope = "defaults";
  private ws: WeaponSettings | null = null;
  private globals: GlobalDefaults | null = null;

  private ours = 0;
  private eqFov = 0;
  /** Defaults tab: live-write the global default while the pill is checked. */
  private defaultLive = true;
  private lastScope: SensScope | null = null;
  private cmValue: string | null = null;
  private fovValue: string | null = null;

  private gameWrap!: HTMLElement;
  private gameBtn!: HTMLButtonElement;
  private gameBtnValue!: HTMLElement;
  private subTitleEl!: HTMLElement;
  private gamePop!: HTMLElement;
  private gameSearch!: HTMLInputElement;
  private gameList!: HTMLElement;
  private gameActive = -1;
  private outsideAbort: AbortController | null = null;
  private dpiEl!: HTMLInputElement;
  private cmEl!: HTMLInputElement;
  private cmSlider!: HTMLInputElement;
  private srcEl!: HTMLInputElement;
  private fovEl!: HTMLInputElement;
  private fovSlider!: HTMLInputElement;
  private fovResultEl!: HTMLElement;
  private infoEl!: HTMLElement;
  private resultEl!: HTMLElement;
  private subEl!: HTMLElement;
  private pillsEl!: HTMLElement;
  private applyBtn: HTMLButtonElement | null = null;
  private fovBtn!: HTMLButtonElement;

  constructor(private cb: SensPickerCallbacks) {}

  /** Build the picker into a freshly created host and initialise it. */
  render(
    host: HTMLElement,
    scope: SensScope,
    ws: WeaponSettings,
    globals: GlobalDefaults,
  ): void {
    this.scope = scope;
    this.ws = ws;
    this.globals = globals;

    const card = el("div", "sens-convert");
    const head = el("div", "sens-head");
    head.append(el("span", "sens-title", "Convert from another game"));
    this.subTitleEl = el("span", "sens-subtitle", this.subtitleText());
    head.append(this.subTitleEl);
    card.append(head);

    const grid = el("div", "sens-grid");

    const gameLabel = el("label", "", "Game");
    // A native <select> with ~170 presets is a long scroll; use a searchable
    // dropdown instead.
    this.gameWrap = el("div", "sens-select");
    this.gameBtn = el("button", "sens-select-btn");
    this.gameBtn.type = "button";
    this.gameBtn.id = "sens-game";
    this.gameBtn.setAttribute("role", "combobox");
    this.gameBtn.setAttribute("aria-haspopup", "listbox");
    this.gameBtn.setAttribute("aria-expanded", "false");
    this.gameBtnValue = el("span", "sens-select-value", this.game);
    this.gameBtn.append(this.gameBtnValue, el("span", "sens-select-caret", "▾"));
    this.gamePop = el("div", "sens-select-pop");
    this.gamePop.id = "sens-game-pop";
    this.gamePop.hidden = true;
    this.gameSearch = el("input", "sens-select-search");
    this.gameSearch.type = "search";
    this.gameSearch.id = "sens-game-search";
    this.gameSearch.placeholder = "Search games…";
    this.gameSearch.autocomplete = "off";
    this.gameSearch.spellcheck = false;
    this.gameList = el("div", "sens-select-list");
    this.gameList.id = "sens-game-list";
    this.gameList.setAttribute("role", "listbox");
    this.gamePop.append(this.gameSearch, this.gameList);
    this.gameWrap.append(this.gameBtn, this.gamePop);
    gameLabel.append(this.gameWrap);

    const dpiLabel = el("label", "", "DPI");
    this.dpiEl = el("input");
    this.dpiEl.id = "sens-dpi";
    this.dpiEl.type = "number";
    this.dpiEl.min = "100";
    this.dpiEl.max = "32000";
    this.dpiEl.step = "1";
    this.dpiEl.value = String(this.dpi);
    dsElement(this.dpiEl, "input", { size: "sm", keep: "w-full" });
    dpiLabel.append(this.dpiEl);

    const cmLabel = el("label", "", "cm/360");
    this.cmEl = el("input");
    this.cmEl.id = "sens-cm";
    this.cmEl.type = "number";
    this.cmEl.min = "0.1";
    this.cmEl.max = "200";
    this.cmEl.step = "0.01";
    this.cmEl.dataset.setting = "controls.sensitivity";
    dsElement(this.cmEl, "input", { size: "sm", keep: "w-full" });
    this.cmSlider = el("input");
    this.cmSlider.id = "sens-cm-slider";
    this.cmSlider.type = "range";
    this.cmSlider.min = "0";
    this.cmSlider.max = "60";
    this.cmSlider.step = "0.1";
    this.cmSlider.dataset.setting = "controls.sensitivity";
    dsElement(this.cmSlider, "range", { keep: "w-full" });
    const spectrumLabels = el("span", "sens-spectrum-labels");
    spectrumLabels.append(el("span", "", "0 cm fast"), el("span", "", "60 cm slow"));
    cmLabel.append(this.cmEl, this.cmSlider, spectrumLabels);

    const srcLabel = el("label", "", "Game sensitivity");
    this.srcEl = el("input");
    this.srcEl.id = "sens-src";
    this.srcEl.type = "number";
    this.srcEl.min = "0.000001";
    this.srcEl.max = "1000";
    this.srcEl.step = "0.000001";
    this.srcEl.dataset.setting = "controls.sensitivity";
    dsElement(this.srcEl, "input", { size: "sm", keep: "w-full" });
    srcLabel.append(this.srcEl);

    const fovLabel = el("label", "", "Source FOV");
    this.fovEl = el("input");
    this.fovEl.id = "sens-fov";
    this.fovEl.type = "number";
    this.fovEl.min = "20";
    this.fovEl.max = "180";
    this.fovEl.step = "0.1";
    this.fovEl.dataset.setting = "video.fov";
    dsElement(this.fovEl, "input", { size: "sm", keep: "w-full" });
    this.fovSlider = el("input");
    this.fovSlider.id = "sens-fov-slider";
    this.fovSlider.type = "range";
    this.fovSlider.min = "20";
    this.fovSlider.max = "180";
    this.fovSlider.step = "0.5";
    this.fovSlider.dataset.setting = "video.fov";
    dsElement(this.fovSlider, "range", { keep: "w-full" });
    fovLabel.append(this.fovEl, this.fovSlider);

    const fovResultLabel = el("label", "", "webrace FOV");
    this.fovResultEl = el("span", "val", "—");
    this.fovResultEl.id = "sens-fov-result";
    fovResultLabel.append(this.fovResultEl);

    grid.append(gameLabel, dpiLabel, cmLabel, srcLabel, fovLabel, fovResultLabel);
    card.append(grid);

    this.infoEl = el("div", "sens-info");
    this.infoEl.id = "sens-game-info";
    card.append(this.infoEl);

    const result = el("div", "sens-result");
    const resultLine = el("div");
    resultLine.append("webrace sensitivity ");
    this.resultEl = el("b", "val", "—");
    this.resultEl.id = "sens-result";
    resultLine.append(this.resultEl);
    this.subEl = el("div", "sens-sub");
    this.subEl.id = "sens-result-sub";
    result.append(resultLine, this.subEl);
    card.append(result);

    // Weapon pills only on the Defaults tab.
    this.pillsEl = el("div", "sens-weapons");
    this.pillsEl.id = "sens-weapons";
    if (scope === "defaults") {
      this.pillsEl.title =
        "Checked = the slider writes your global sensitivity live. Uncheck to freeze the default while you pin weapons; tagged weapons keep their own value.";
      card.append(this.pillsEl);
    }

    const actions = el("div", "row sens-actions");
    if (scope !== "defaults") {
      // On Defaults the "Default" pill applies live, so there is no button.
      this.applyBtn = el("button", "", "Use for this weapon");
      this.applyBtn.id = "sens-apply";
      dsElement(this.applyBtn, "button", { style: "outline", size: "sm" });
      actions.append(this.applyBtn);
    }
    this.fovBtn = el("button", "secondary", "Use FOV");
    this.fovBtn.id = "sens-apply-fov";
    dsElement(this.fovBtn, "button", { style: "outline", size: "sm" });
    actions.append(this.fovBtn);
    card.append(actions);

    if (this.fovBtn) {
      this.fovBtn.title =
        "Copies the source FOV into the game FOV so cm/360 matches. Zoom sensitivity stays per-weapon in this tab.";
    }

    host.append(card);
    this.wire();
    this.initValues();
  }

  /** Re-render just the pills after a pin/unpin. */
  refreshPills(): void {
    this.renderPills();
  }

  private scopeSens(): number {
    if (!this.ws || !this.globals) return 0;
    if (this.scope === "defaults") return this.globals.sensitivity;
    return resolveWeapon(this.ws, this.globals, this.scope).sensitivity;
  }

  private gameEntry(): readonly [string, number, number?, number?, SensFovType?] | null {
    return SENS_GAMES.find((g) => g[0] === this.game) ?? null;
  }

  private initValues(): void {
    const game = this.gameEntry();
    if (!game || !this.globals) return;
    this.dpiEl.value = String(this.dpi);
    const sameScope = this.lastScope === this.scope && this.cmValue !== null;
    if (sameScope) {
      // A re-render (FOV apply, settings sync, weapon-panel refresh) must not
      // wipe what the player is typing.
      this.cmEl.value = this.cmValue!;
    } else {
      this.cmEl.value = sensToCm360(this.scopeSens(), this.dpi, game[1]).toFixed(2);
      this.last = "cm";
    }
    const fov =
      sameScope && this.fovValue !== null
        ? Number(this.fovValue)
        : game[0] === "webrace"
          ? this.globals.fov
          : defaultGameFov(game[2], game[3], game[4]);
    if (fov) {
      this.fovEl.value = Number(fov).toFixed(2);
      this.fovSlider.value = String(clamp(Number(fov), 20, 180));
    }
    this.lastScope = this.scope;
    this.update();
  }

  private wire(): void {
    // Searchable game dropdown.
    this.gameBtn.addEventListener("click", () => {
      if (this.gamePop.hidden) this.openGameMenu();
      else this.closeGameMenu();
    });
    this.gameWrap.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && !this.gamePop.hidden) {
        e.preventDefault();
        this.closeGameMenu();
        this.gameBtn.focus();
      }
    });
    this.gameSearch.addEventListener("input", () => {
      this.renderGameOptions(this.gameSearch.value);
    });
    this.gameSearch.addEventListener("keydown", (e) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        this.setGameActive(this.gameActive + 1);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        this.setGameActive(this.gameActive - 1);
      } else if (e.key === "Enter") {
        e.preventDefault();
        const opts = this.gameOptions();
        const pick = opts[this.gameActive] ?? opts[0];
        if (pick?.dataset.game) this.chooseGame(pick.dataset.game);
      } else if (e.key === "Escape") {
        e.preventDefault();
        this.closeGameMenu();
        this.gameBtn.focus();
      }
    });
    this.gameList.addEventListener("click", (e) => {
      const b = (e.target as HTMLElement).closest<HTMLButtonElement>(".sens-select-opt");
      if (b?.dataset.game) this.chooseGame(b.dataset.game);
    });
    this.outsideAbort?.abort();
    this.outsideAbort = new AbortController();
    document.addEventListener(
      "pointerdown",
      (e) => {
        if (!this.gameWrap.isConnected) return;
        if (!this.gameWrap.contains(e.target as Node)) this.closeGameMenu();
      },
      { signal: this.outsideAbort.signal },
    );
    this.dpiEl.addEventListener("input", () => {
      this.dpi = clamp(Number(this.dpiEl.value) || 1600, 100, 32000);
      this.update(true);
    });
    this.cmEl.addEventListener("input", () => {
      this.last = "cm";
      this.update(true);
    });
    this.cmSlider.addEventListener("input", () => {
      this.cmEl.value = this.cmSlider.value;
      this.last = "cm";
      this.update(true);
    });
    this.srcEl.addEventListener("input", () => {
      this.last = "sens";
      this.update(true);
    });
    this.fovEl.addEventListener("input", () => {
      this.applyLinkedFov();
      this.updateFov();
    });
    this.fovSlider.addEventListener("input", () => {
      this.fovEl.value = this.fovSlider.value;
      this.applyLinkedFov();
      this.updateFov();
    });
    this.applyBtn?.addEventListener("click", () => {
      if (this.ours > 0) this.cb.onApply(this.scope, clamp(Number(this.ours.toFixed(6)), 0.05, 50));
    });
    this.fovBtn.addEventListener("click", () => {
      if (this.eqFov > 0) this.cb.onFov(Math.round(clamp(this.eqFov, 60, 160)));
    });
  }

  private subtitleText(): string {
    const g = SENS_GAMES.find((x) => x[0] === this.game);
    return g ? `${g[0]} · yaw ${g[1]}` : this.game;
  }

  private gameOptions(): HTMLButtonElement[] {
    return Array.from(this.gameList.querySelectorAll<HTMLButtonElement>(".sens-select-opt"));
  }

  private setGameActive(index: number): void {
    const opts = this.gameOptions();
    if (!opts.length) {
      this.gameActive = -1;
      return;
    }
    this.gameActive = ((index % opts.length) + opts.length) % opts.length;
    opts.forEach((b, i) => b.classList.toggle("active", i === this.gameActive));
    const active = opts[this.gameActive];
    const list = this.gameList;
    if (active.offsetTop < list.scrollTop) list.scrollTop = active.offsetTop;
    else if (active.offsetTop + active.offsetHeight > list.scrollTop + list.clientHeight) {
      list.scrollTop = active.offsetTop + active.offsetHeight - list.clientHeight;
    }
  }

  private renderGameOptions(query: string): void {
    this.gameList.replaceChildren();
    const q = query.trim().toLowerCase();
    const games = q ? SENS_GAMES.filter((g) => g[0].toLowerCase().includes(q)) : SENS_GAMES;
    if (!games.length) {
      this.gameList.append(el("div", "sens-select-empty", "No matching game"));
      this.gameActive = -1;
      return;
    }
    for (const [name] of games) {
      const b = el("button", "sens-select-opt", name);
      b.type = "button";
      b.dataset.game = name;
      b.setAttribute("role", "option");
      if (name === this.game) {
        b.classList.add("current");
        b.setAttribute("aria-selected", "true");
      }
      this.gameList.append(b);
    }
    const current = games.findIndex((g) => g[0] === this.game);
    this.setGameActive(current >= 0 ? current : 0);
  }

  private openGameMenu(): void {
    this.gamePop.hidden = false;
    this.gameBtn.setAttribute("aria-expanded", "true");
    this.gameSearch.value = "";
    this.renderGameOptions("");
    this.gameSearch.focus();
  }

  private closeGameMenu(): void {
    if (!this.gamePop) return;
    this.gamePop.hidden = true;
    this.gameBtn.setAttribute("aria-expanded", "false");
  }

  private chooseGame(name: string): void {
    this.game = name;
    this.gameBtnValue.textContent = name;
    this.closeGameMenu();
    this.gameBtn.focus();
    const game = this.gameEntry();
    if (game) {
      const fov =
        game[0] === "webrace" ? (this.globals?.fov ?? 125) : defaultGameFov(game[2], game[3], game[4]);
      if (fov) {
        this.fovEl.value = fov.toFixed(2);
        this.fovSlider.value = String(clamp(fov, 20, 180));
      }
    }
    this.update(true);
  }

  /** `live` is true for real user edits (not panel re-render / init). */
  private update(live = false): void {
    const game = this.gameEntry();
    if (!game) return;
    if (this.subTitleEl) this.subTitleEl.textContent = this.subtitleText();
    const yaw = game[1];
    const dpi = this.dpi;

    let cm360: number;
    let srcSens: number;
    if (this.last === "sens") {
      srcSens = clamp(Number(this.srcEl.value) || 1, 0.000001, 1000);
      cm360 = sensToCm360(srcSens, dpi, yaw);
      this.cmEl.value = cm360.toFixed(2);
    } else {
      const raw = Number(this.cmEl.value);
      cm360 = Number.isFinite(raw) ? clamp(raw, 0.1, 200) : 30;
      if (raw !== cm360) this.cmEl.value = cm360.toFixed(2);
      srcSens = cm360ToSens(cm360, dpi, yaw);
      this.srcEl.value = numText(srcSens);
    }
    this.cmSlider.value = String(clamp(cm360, 0, 60));
    this.cmValue = this.cmEl.value;

    this.ours = toOurSensitivity(srcSens, yaw);
    this.resultEl.textContent = this.ours.toFixed(4);
    this.subEl.textContent =
      `${cm360.toFixed(2)} cm/360 · ${Math.round(srcSens * dpi)} source eDPI` +
      ` · webrace sens ${numText(this.ours)} (${Math.round(this.ours * dpi)} eDPI)`;

    this.updateFov();
    this.renderPills();
    if (live) this.applyLiveDefault();
  }

  /** Defaults tab + checked Default pill: write the picker value as the global. */
  private applyLiveDefault(): void {
    if (this.scope !== "defaults" || !this.defaultLive || !this.globals) return;
    const v = clamp(Number(this.ours.toFixed(6)), 0.05, 50);
    if (Math.abs(v - this.globals.sensitivity) < 1e-9) return;
    this.globals.sensitivity = v;
    this.cb.onApply("defaults", v);
  }

  private updateFov(): void {
    const game = this.gameEntry();
    if (!game || !this.globals) return;
    const isOurs = game[0] === "webrace";
    const aspect = window.innerWidth / Math.max(1, window.innerHeight);
    const fov = clamp(
      Number(this.fovEl.value) ||
        (isOurs ? this.globals.fov : defaultGameFov(game[2], game[3], game[4])) ||
        90,
      20,
      180,
    );
    this.fovSlider.value = String(clamp(fov, 20, 180));
    this.fovValue = this.fovEl.value;
    if (isOurs) {
      this.eqFov = fov;
      this.fovResultEl.textContent = `${fov.toFixed(1)}° (linked)`;
      this.infoEl.textContent =
        `webrace · yaw ${game[1]} · input FOV ${fov.toFixed(1)}° (in-game FOV)` +
        ` · current ${this.globals.fov}°`;
    } else {
      this.eqFov = ourFov(fov, game[4], aspect);
      this.fovResultEl.textContent = `${this.eqFov.toFixed(1)}° (${aspect.toFixed(2)}:1)`;
      const vf = game[2] ? `${game[2].toFixed(2)}°` : "—";
      const hf = game[3] ? `${game[3].toFixed(2)}°` : "—";
      this.infoEl.textContent =
        `${game[0]} · yaw ${game[1]} · VFOV ${vf} · HFOV ${hf}` +
        ` · source FOV ${fov.toFixed(1)}° → VFOV ${fovToVfov(fov, game[4]).toFixed(1)}°`;
    }
  }

  /** webrace preset: the source FOV is the in-game FOV, so keep them linked. */
  private applyLinkedFov(): void {
    if (this.gameEntry()?.[0] !== "webrace") return;
    const fov = Math.round(clamp(Number(this.fovEl.value) || this.globals?.fov || 125, 60, 160));
    if (this.globals && fov !== this.globals.fov) this.cb.onFov(fov);
  }

  private renderPills(): void {
    if (this.scope !== "defaults" || !this.pillsEl || !this.ws) return;
    this.pillsEl.replaceChildren();

    // The Default pill is first and checked out of the box: slider edits write
    // the global sensitivity until the player unchecks it.
    const def = el("button", "sens-weapon sens-default", "Default");
    def.type = "button";
    def.classList.toggle("active", this.defaultLive);
    def.title = this.defaultLive
      ? "Default sensitivity follows the slider — click to freeze it"
      : "Click to make slider changes update the default sensitivity again";
    def.addEventListener("click", () => {
      this.defaultLive = !this.defaultLive;
      if (this.defaultLive) this.applyLiveDefault();
      this.renderPills();
    });
    this.pillsEl.append(def);

    for (const [id, name] of WEAPONS) {
      const own = this.ws.overrides[String(id)];
      const sens = own && typeof own.sensitivity === "number" ? own.sensitivity : null;
      const b = el("button", "sens-weapon", name);
      b.type = "button";
      if (sens !== null) {
        const cm = sensToCm360(sens, this.dpi, OUR_YAW);
        b.textContent = `${name} · ${cm.toFixed(1)}cm`;
        b.title = `pinned at ${sens.toFixed(4)} (${cm.toFixed(2)} cm/360) — click to unpin`;
        b.classList.add("active");
      } else {
        b.title = "pin this weapon to the current sensitivity";
      }
      b.addEventListener("click", () => {
        if (!this.ws) return;
        if (sens !== null) {
          this.cb.onPin(id, null);
        } else {
          this.cb.onPin(id, clamp(Number(this.ours.toFixed(6)), 0.05, 50));
        }
        this.renderPills();
      });
      this.pillsEl.append(b);
    }
  }
}
